/**
 * 결제 API 서비스
 *
 * @see olulo-mx-admin/app/Http/Controllers/Api/V1/PaymentController.php
 */
import { apiClient, validatePathParam } from './client';
import type {
  CreatePaymentRequest,
  PaymentCreateResponse,
  PaymentResponse,
  PaymentStatusResponse,
  CancelPaymentRequest,
  CancelPaymentResponse,
  ChargePaymentRequest,
  ChargePaymentResponse,
  ChargeSessionRequest,
  ChargeSessionResponse,
  Payment,
  PostpayChargeBundleData,
  SessionUnpaidPostpayResponse,
} from './types';

/**
 * admin A2 /charge 의 원본 응답 (snake_case 중첩).
 * `chargePayment` 가 FE camelCase 로 정규화하기 전 shape.
 *
 * @internal
 */
interface ChargePaymentRawResponse {
  payment: Payment;
  gateway?: 'bloomin';
  bloomin?: {
    challenge_url?: string | null;
    reference_3d?: string | null;
  };
}

/**
 * admin R5 세션 합산청구 /charge 의 원본 응답 (snake_case 중첩).
 * `chargeSession` 이 FE camelCase 로 정규화하기 전 shape.
 *
 * @internal
 */
interface ChargeSessionRawResponse {
  bundle: PostpayChargeBundleData;
  bloomin?: {
    challenge_url?: string | null;
    reference_3d?: string | null;
  };
}

interface TableSessionPaymentRequestResponse {
  success: boolean;
  message?: string;
  data: {
    id: number;
    table_session_id: number;
    bill_id: number | null;
    status: string;
    status_label: string;
    requested_method: TableSessionPaymentRequestMethod;
    requested_method_label: string;
    amount_snapshot: number;
    tip_amount: number;
    order_count_snapshot: number;
    created_at: string;
  };
}

export type TableSessionPaymentRequestMethod = 'cash' | 'pos_card_reader';

export const paymentApi = {
  /**
   * 결제 생성
   * POST /api/v1/orders/:orderId/payments
   *
   * 카드 결제: Bloomin 3DS 입력/챌린지 정보를 반환
   * 현금/이체 결제: pending 상태로 생성 (매장 확인 필요)
   *
   * @param orderId - 주문 ID
   * @param data - 결제 요청 데이터
   */
  createPayment: (orderId: string | number, data: CreatePaymentRequest) =>
    apiClient.post<PaymentCreateResponse>(
      `/api/v1/orders/${orderId}/payments`,
      data
    ),

  /**
   * 결제 상세 조회
   * GET /api/v1/orders/:orderId/payments/:paymentId
   *
   * 카드 결제: 서버 권위 상태 조회
   * 회원 주문: 인증 필수
   * 비회원 주문: guest_password 필요 (query param)
   */
  getPayment: (
    orderId: string | number,
    paymentId: string | number,
    guestPassword?: string
  ) =>
    apiClient.get<PaymentResponse>(
      `/api/v1/orders/${orderId}/payments/${paymentId}`,
      {
        params: guestPassword ? { guest_password: guestPassword } : undefined,
      }
    ),

  /**
   * 결제 상태 폴링
   * POST /api/v1/orders/:orderId/payments/:paymentId/status
   *
   * 간소화된 상태 응답 (폴링 최적화)
   * 비회원: guest_password를 body로 전달
   */
  pollStatus: (
    orderId: string | number,
    paymentId: string | number,
    guestPassword?: string
  ) =>
    apiClient.post<PaymentStatusResponse>(
      `/api/v1/orders/${orderId}/payments/${paymentId}/status`,
      { guest_password: guestPassword }
    ),

  /**
   * 결제 취소
   * POST /api/v1/orders/:orderId/payments/:paymentId/cancel
   *
   * pending 상태의 결제만 취소 가능
   */
  cancelPayment: (
    orderId: string | number,
    paymentId: string | number,
    data?: CancelPaymentRequest
  ) =>
    apiClient.post<CancelPaymentResponse>(
      `/api/v1/orders/${orderId}/payments/${paymentId}/cancel`,
      data
    ),

  /**
   * 후불 "지금 결제" 카드 결제 (C3 #590)
   * POST /api/v1/orders/:orderId/payments/:paymentId/charge
   *
   * 후불 매장에서 이미 생성된 pending 결제에 카드 정보를 제출해 결제를 진행한다.
   * admin A2 응답의 `bloomin.challenge_url` / `bloomin.reference_3d` (snake) 를
   * `challengeUrl` / `reference3D` (camel) 로 정규화한다.
   *
   * 에러: 409 PAYMENT_NOT_SETTLEABLE / 422 validation / 403 / 404 등은
   * apiClient 가 ApiClientError(code, status) 로 전파한다.
   *
   * @param orderId - 주문 ID
   * @param paymentId - 결제 ID
   * @param data - 카드 정보 (+ 비회원 guest_password)
   */
  chargePayment: async (
    orderId: string | number,
    paymentId: string | number,
    data: ChargePaymentRequest
  ): Promise<ChargePaymentResponse> => {
    const raw = await apiClient.post<ChargePaymentRawResponse>(
      `/api/v1/orders/${orderId}/payments/${paymentId}/charge`,
      data
    );

    return {
      payment: raw.payment,
      gateway: raw.gateway,
      bloomin: raw.bloomin?.challenge_url
        ? {
            challengeUrl: raw.bloomin.challenge_url,
            reference3D: raw.bloomin.reference_3d ?? null,
          }
        : undefined,
    };
  },
};

/**
 * 테이블 세션 합산청구 API (#1705 R8 — 사용자 최초 요청의 최종 구현).
 *
 * 후불 매장에서 식후 정산 시 세션 미결제 후불 주문 N건을 1회 Bloomin 3DS 로 합산 청구한다.
 * admin R5 (`TableSessionChargeController`) 가 노출한 계약을 소비한다.
 *
 *  - GET  /api/v1/table-sessions/{token}/unpaid-postpay → 세션 미결제 후불 목록 + 서버 합산
 *  - POST /api/v1/table-sessions/{token}/charge → 합산청구(번들) + Bloomin 3DS challenge
 *
 * 인증(R5): 비회원은 GET 은 `X-Guest-Password` 헤더 / POST 는 body `guest_password`.
 * 회원은 apiClient 의 Authorization 헤더(자동). lockout 5회(token+IP).
 *
 * @see olulo-mx-admin App\Http\Controllers\Api\V1\TableSessionChargeController
 */
export const tableSessionApi = {
  /**
   * 세션 미결제 후불 주문 목록 + 합계 조회 (#1705 R8).
   * GET /api/v1/table-sessions/{token}/unpaid-postpay.
   *
   * 비회원은 guest_password 를 `X-Guest-Password` 헤더로 전달한다(R5 — GET 은 헤더 전용,
   * 쿼리스트링/로그 secret 노출 방지). 회원은 헤더 없이 Authorization 으로 인증된다.
   *
   * @param token - 테이블 세션 토큰 (sess_...)
   * @param guestPassword - 비회원 주문 비밀번호 (있으면 X-Guest-Password 헤더 부착)
   */
  getSessionUnpaidPostpay: async (
    token: string,
    guestPassword?: string,
    scope?: { billId?: number; comensalDeviceToken?: string },
  ): Promise<SessionUnpaidPostpayResponse> => {
    // Codex Important 2: token path param 검증/인코딩 (route 오염 방어).
    // async 로 선언해 validatePathParam 의 동기 throw 도 rejected promise 로 전파한다
    // (호출자 OrderHistoryPage 가 .catch() 로 fail-closed 처리 — 동기 throw 시 effect 크래시 방지).
    const safeToken = validatePathParam(token, 'token');

    return apiClient.get<SessionUnpaidPostpayResponse>(
      `/api/v1/table-sessions/${safeToken}/unpaid-postpay`,
      {
        params: scope?.billId === undefined ? undefined : { bill_id: scope.billId },
        headers: {
          ...(guestPassword ? { 'X-Guest-Password': guestPassword } : {}),
          ...(scope?.comensalDeviceToken
            ? { 'X-Comensal-Token': scope.comensalDeviceToken }
            : {}),
        },
      }
    );
  },

  /**
   * 세션 합산청구(번들) — 미결제 후불 N건을 1회 Bloomin 3DS 로 청구 (#1705 R8).
   * POST /api/v1/table-sessions/{token}/charge.
   *
   * admin R5 응답의 `bloomin.challenge_url` / `bloomin.reference_3d` (snake) 를
   * `challengeUrl` / `reference3D` (camel) 로 정규화한다(단건 chargePayment 동형).
   * 비회원 guest_password 는 body 로 동봉(R5 — POST 는 body 전용).
   *
   * 에러: 409/422/403/404/429 등은 apiClient 가 ApiClientError(code, status) 로 전파한다.
   *
   * @param token - 테이블 세션 토큰 (sess_...)
   * @param data - 카드 정보 + 주문별 보정치 (+ 비회원 guest_password)
   */
  chargeSession: async (
    token: string,
    data: ChargeSessionRequest,
    comensalDeviceToken?: string,
  ): Promise<ChargeSessionResponse> => {
    // Codex Important 2: token path param 검증/인코딩 (money-path route 오염 방어).
    const safeToken = validatePathParam(token, 'token');

    const raw = await apiClient.post<ChargeSessionRawResponse>(
      `/api/v1/table-sessions/${safeToken}/charge`,
      data,
      comensalDeviceToken
        ? { headers: { 'X-Comensal-Token': comensalDeviceToken } }
        : undefined,
    );

    return {
      bundle: raw.bundle,
      bloomin: raw.bloomin?.challenge_url
        ? {
            challengeUrl: raw.bloomin.challenge_url,
            reference3D: raw.bloomin.reference_3d ?? null,
          }
      : undefined,
    };
  },

  /**
   * 고객 현장 결제 요청.
   * POST /api/v1/table-sessions/{token}/payment-requests
   *
   * 실제 paid 처리는 POS/manual settlement 흐름이 담당하며, 이 API는 POS 운영 화면에
   * 별도 결제 요청 도메인 상태를 만든다.
   *
   * ## guest_password 가 필수인 이유 (admin #2231)
   *
   * 이 엔드포인트는 원래 인가가 **전혀 없었다**. 세션 토큰만 알면 누구나 남의 테이블에
   * 결제 요청을 만들어 정당한 카드 청구를 409 로 막을 수 있었다(denial-of-payment).
   * admin 이 `{token}/charge` 와 동일한 인가 경계를 붙이면서, 비회원은 이제
   * guest_password 를 증명해야 한다 — 안 보내면 403 이다.
   *
   * 회원은 Authorization 헤더로 인증되므로 이 값이 필요 없다.
   * POST 이므로 body 로 보낸다(charge 와 동형 — GET 만 `X-Guest-Password` 헤더).
   */
  requestPaymentRequest: async (
    token: string,
    method: TableSessionPaymentRequestMethod,
    guestPassword?: string,
    scope?: {
      billId?: number;
      comensalDeviceToken?: string;
      expectedBillSubtotalMinor?: number;
      expectedTotalMinor?: number;
      tipAmount?: number;
    },
  ): Promise<TableSessionPaymentRequestResponse> => {
    const safeToken = validatePathParam(token, 'token');

    return apiClient.post<TableSessionPaymentRequestResponse>(
      `/api/v1/table-sessions/${safeToken}/payment-requests`,
      {
        method,
        ...(guestPassword ? { guest_password: guestPassword } : {}),
        ...(scope?.billId === undefined ? {} : { bill_id: scope.billId }),
        ...(scope?.expectedBillSubtotalMinor === undefined
          ? {}
          : { expected_bill_subtotal_minor: scope.expectedBillSubtotalMinor }),
        ...(scope?.expectedTotalMinor === undefined
          ? {}
          : { expected_total_minor: scope.expectedTotalMinor }),
        ...(scope?.tipAmount === undefined ? {} : { tip_amount: scope.tipAmount }),
      },
      scope?.comensalDeviceToken
        ? { headers: { 'X-Comensal-Token': scope.comensalDeviceToken } }
        : undefined,
    );
  },

  requestCashPayment: async (
    token: string,
    guestPassword?: string,
    scope?: {
      billId?: number;
      comensalDeviceToken?: string;
      expectedBillSubtotalMinor?: number;
      expectedTotalMinor?: number;
      tipAmount?: number;
    },
  ): Promise<TableSessionPaymentRequestResponse> =>
    tableSessionApi.requestPaymentRequest(token, 'cash', guestPassword, scope),
};
